import { useState } from "react";
import { ImagePlus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import FileDropzone from "@/components/FileDropzone";
import {
  ACCEPTED_IMAGE_TYPES,
  keepValidImages,
  updateBrickSeries,
  uploadImage,
} from "../../utils";
import useOrganization from "../../hooks/useOrganization";
import type { BrickSeries } from "../../masterImages/types";

interface Props {
  brickSeries: BrickSeries;
  onUpdate(newSeries: BrickSeries): void;
}

const UploadThumbnail: React.FC<Props> = ({ brickSeries, onUpdate }) => {
  const organization = useOrganization();
  const [progress, setProgress] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);

  const upload = async (files: File[]) => {
    const [file] = await keepValidImages(files);
    if (!file) return;
    setProgress(0);
    try {
      const key = await uploadImage(
        organization,
        brickSeries._id,
        "thumbnail",
        file,
        setProgress,
      );
      onUpdate(await updateBrickSeries(organization, brickSeries._id, { thumbnail: key }));
    } catch (e) {
      toast.error((e as Error).message || "Uploaden mislukt");
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await updateBrickSeries(organization, brickSeries._id, { thumbnail: null });
      onUpdate({ ...brickSeries, thumbnail: null });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  const busy = progress != null || removing;

  return (
    <div className="grid w-40 gap-2">
      <FileDropzone
        accept={ACCEPTED_IMAGE_TYPES}
        disabled={busy}
        onFiles={upload}
        aria-label={brickSeries.thumbnail ? "Thumbnail vervangen" : "Thumbnail uploaden"}
        className="relative aspect-square w-40 overflow-hidden p-0"
      >
        {brickSeries.thumbnail ? (
          <img src={brickSeries.thumbnail} alt="" className="size-full object-cover" />
        ) : (
          <>
            <ImagePlus className="size-6" />
            <span className="px-3">Klik of sleep een afbeelding</span>
          </>
        )}
        {progress != null && (
          <span className="absolute inset-x-3 bottom-3">
            <Progress value={progress} aria-label="Uploaden" />
          </span>
        )}
      </FileDropzone>
      {brickSeries.thumbnail && (
        <div className="flex gap-1">
          <FileDropzone
            accept={ACCEPTED_IMAGE_TYPES}
            disabled={busy}
            onFiles={upload}
            className="h-8 flex-1 flex-row rounded-md border-solid bg-background text-xs font-medium text-foreground"
          >
            <RefreshCw className="size-3.5" />
            Vervangen
          </FileDropzone>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={busy}
            onClick={remove}
            aria-label="Thumbnail verwijderen"
            title="Thumbnail verwijderen"
          >
            <Trash2 />
          </Button>
        </div>
      )}
    </div>
  );
};

export default UploadThumbnail;
